import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { getConfigValue, getConfigCollection } from '../util/getConfigValue';
import { AppContext } from '../util/AppContext';
import { getSanitizedHtml } from '../util/getSanitizedHtml';
import PlanListRow from './PlanListRow';
import Mustache from 'mustache';
import { compareForDynamicTable } from '../util/dynamicTableSort.js';
import { getTableColumnsSpec } from '../util/getTableColumnsSpec.js';
import { isPlanTableMaxHeightExceeded } from '../util/dynamicPlanTableHeight.js';
import Tooltip from './Tooltip';
import { hcpType } from '../util/constants';

PlanListTable.propTypes = {
	updateSelectedPlans: PropTypes.func,
	message: PropTypes.object,
	parentState: PropTypes.shape({
		isLoading: PropTypes.bool,
		isSingleSelection: PropTypes.bool,
		savingsCardIsChecked: PropTypes.bool,
		selectionMessage: PropTypes.string,
		exceedMaxColumns: PropTypes.bool,
		maxDisplayColumns: PropTypes.number,
		isImprovedAccessStatic: PropTypes.bool,
		selectableOptions: PropTypes.shape({
			canSelectFormulary: PropTypes.bool,
			canSelectRestrictions: PropTypes.bool,
			canSelectSpecialtyPharmacy: PropTypes.bool,
			canSelectSavingsCard: PropTypes.bool,
		}),
		isSingleOptionSelectionEnabled: PropTypes.bool,
		isSingleOptionFormularyDrillDownEnabled: PropTypes.bool,
		isSingleOptionRestrictionDetailsEnabled: PropTypes.bool,
		isSingleOptionSpecialtyPharmacyEnabled: PropTypes.bool,
		isSingleOptionSavingsCardEnabled: PropTypes.bool,
		selectedPlans: PropTypes.array,
		preSelectedPlans: PropTypes.array
	}),
	setSelectableOptions: PropTypes.func,
	triggerToastAlert: PropTypes.func,
	executePrintOptionRestrictions: PropTypes.func,
	showTooManyOptionsSelectedMessage: PropTypes.func,
	selectAllEnabled: PropTypes.bool,
	selectAllChecked: PropTypes.bool,
	onSelectAllClick: PropTypes.func,
	isContainerMessage: PropTypes.bool
};

function PlanListTable({
	updateSelectedPlans,
	message,
	parentState,
	setSelectableOptions,
	triggerToastAlert,
	executePrintOptionRestrictions,
	showTooManyOptionsSelectedMessage,
	selectAllEnabled,
	selectAllChecked,
	onSelectAllClick,
	isContainerMessage
}) {
	const [appState] = useContext(AppContext);
	const [plans, setPlans] = useState(message.dynamicTable);
	const [isExpandAll, setIsExpandAll] = useState(false);
	const [toggleCollapse, setToggleCollapse] = useState(false);
	const [selectedPlans, setSelectedPlans] = useState([]);
	const [columnsSpec, setColumnsSpec] = useState('');
	const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
	const isSyncingFromParent = React.useRef(false);

	const formularyDrillDownEnabled = getConfigValue(appState.config.Formulary?.DrillDown?.Enabled, appState)?.Value === 'true';
	const restrictionDrillDownEnabled = getConfigValue(appState.config.Restriction?.DrillDown?.Enabled, appState)?.Value === 'true';
	const specialtyPharmacyDrillDownEnabled = getConfigValue(appState.config.SpecialtyPharmacy?.DrillDown?.Enabled, appState)?.Value === 'true';
	const autoSelectFormularyDrillDownEnabled = getConfigValue(appState.config?.Planlist?.FormularyDrillDown?.AutoSelect?.Enabled, appState)?.Value === 'true';
	const autoSelectRestrictionDetailsEnabled = getConfigValue(appState.config?.Planlist?.RestrictionDetails?.AutoSelect?.Enabled, appState)?.Value === 'true';
	const autoSelectSpecialtyPharmacyEnabled = getConfigValue(appState.config?.Planlist?.SpecialtyPharmacy?.AutoSelect?.Enabled, appState)?.Value === 'true';
	const planListConfig = useMemo(() => getConfigCollection(appState.config?.PlanList, appState, 'PlanList'), [appState]);
	const maxPlans = getConfigValue(appState.config.PlanList.MaxPrintablePlans, appState)?.Value;
	const enableEllipsisButton = getConfigValue(appState.config.PlanList.Options.EllipsisMenu, appState)?.Value === 'true';
	const enableInlineIcons = getConfigValue(appState.config?.PlanList?.InlineIcons?.Enabled, appState)?.Value === 'true';
	const rejectClaimEnabled = getConfigValue(appState.config?.RejectClaim?.Enabled, appState)?.Value === 'true' && appState?.prescriberType === hcpType.Prescriber;

	const rejectClaimHeaderTooltipRef = useRef(null);
	const [showRejectClaimHeaderTooltip, setShowRejectClaimHeaderTooltip] = useState(false);

	const includeRestrictionNotes = getConfigValue(appState.config.PlanList.IncludeRestrictionNotesInHeightCalc, appState)?.Value === 'true';
	const maxTableHeightInPx = getConfigValue(appState.config.PlanList.MaxTableHeight, appState)?.Value;
	
	

	useEffect(() => {
		function handleResize() {
			setViewport({ width: window.innerWidth, height: window.innerHeight });
		}
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (isSyncingFromParent.current) {
			isSyncingFromParent.current = false;
			return;
		}
		updateSelectedPlans(message.prescriberMessage.messageCode, selectedPlans);
	}, [selectedPlans, message]);

	useEffect(() => {
		if (parentState.preSelectedPlans && parentState.preSelectedPlans.length > 0) {
			setSelectedPlans(parentState.preSelectedPlans);
		}
	}, [parentState.preSelectedPlans]);

	const parentSelectedPlanIds = useMemo(
		() => parentState.selectedPlans?.map(p => p.planId).sort().join(',') || '',
		[parentState.selectedPlans]
	);

	useEffect(() => {
		//Container messages do not support the Select All and therefore this update from the parent
		if(isContainerMessage){
			return;
		}
		
		const currentPlanIds = selectedPlans.map(p => p.planId).sort().join(',') || '';
		
		if (parentSelectedPlanIds !== currentPlanIds ) {
			isSyncingFromParent.current = true;
			setSelectedPlans(parentState.selectedPlans || []);
		}
	}, [parentSelectedPlanIds, isContainerMessage]);

	useEffect(() => {
		// force a reset of the single option statuses when a plan is deselected
		executePrintOptionRestrictions({}, parentState, setSelectableOptions, showTooManyOptionsSelectedMessage);
	}, [JSON.stringify(parentState.selectedPlans)]);

	useEffect(() => {
		setPlans(message.dynamicTable);
		setSelectedPlans([]);
	}, [message, parentState.maxDisplayColumns]);

	useEffect(() => {
		if (viewport.width < 990) {
			setColumnsSpec('1fr');
			return;
		}
		setColumnsSpec(getTableColumnsSpec(message.dynamicTable.headers.slice(0, parentState.maxDisplayColumns)) + (rejectClaimEnabled ? ' minmax(140px, 1fr)' : '') + ' 75px');
	}, [message, parentState.maxDisplayColumns, viewport, rejectClaimEnabled]);

	function updateCollapseExpand(expand) {
		setIsExpandAll(expand);
		setToggleCollapse(!toggleCollapse);
	}

	function handleCheckboxClick(plan, event) {
		const isChecked = event.target.checked;
		let maxReached = maxPlans ? selectedPlans.length >= maxPlans : false;
		if(!maxReached && maxTableHeightInPx && isChecked){
			maxReached = isPlanTableMaxHeightExceeded(selectedPlans, plans.rows, maxTableHeightInPx, plan.planId, includeRestrictionNotes);
		}

		if (parentState.isLoading) {
			event.preventDefault();
			return;
		}

		const autoSelectFormulary = autoSelectFormularyDrillDownEnabled && plan.formularyDrillDown?.length > 0;
		const autoSelectRestrictions = autoSelectRestrictionDetailsEnabled && plan.formularyRestriction?.length > 0;
		const autoSelectSpecialtyPharmacy = autoSelectSpecialtyPharmacyEnabled && plan.specialtyPharmacies?.length > 0;

		if (parentState.isSingleSelection && isChecked) {
			setSelectedPlans([{
				...plan,
				includeFormularies: autoSelectFormulary,
				includeRestrictions: autoSelectRestrictions,
				includeSpecialtyPharmacy: autoSelectSpecialtyPharmacy,
			}]);
			return;
		}

		if (!isChecked) {
			setSelectedPlans((originalPlans) => originalPlans.filter((p) => p.planId !== plan.planId));
		}

		if (isChecked && !maxReached) {
			const planWithOptions = {
				...plan,
				includeFormularies: autoSelectFormulary && checkMaxFormularyPlans({ ...plan, includeFormularies: true }),
				includeRestrictions: autoSelectRestrictions && checkMaxRestrictionPlans({ ...plan, includeRestrictions: true }),
				includeSpecialtyPharmacy: autoSelectSpecialtyPharmacy && checkMaxSpecialtyPlans({ ...plan, includeSpecialtyPharmacy: true }),
			};
			setSelectedPlans((originalPlans) => [...originalPlans, planWithOptions]);
		}

		if (isChecked && maxReached) {
			event.preventDefault();
			showMaxPlansMessage();
		}

	}

	function onOptionsClick(data) {
		if (!executePrintOptionRestrictions(data, parentState, setSelectableOptions, showTooManyOptionsSelectedMessage)) {
			return false;
		}

		const toInclude = data.includeFormularies || data.includeRestrictions || data.includeSpecialtyPharmacy || data.includeCompetitiveRestrictionNotes?.filter((p) => p.includeRestrictions).length > 0;

		// handle single selection, if something is checked, replaces the one selected plan
		if (parentState.isSingleSelection) {
			if (toInclude) {
				setSelectedPlans([data]);
			}
			return true;
		}

		// handle multiple selection
		const plan = selectedPlans.find((p) => p.planId === data.planId);
		if (!plan && toInclude) {
			// if max plans reached, don't allow adding more
			if (maxPlans && selectedPlans.length >= maxPlans) {
				showMaxPlansMessage();
				return false;
			}
			if (maxTableHeightInPx) {
				const heightMaxReached = isPlanTableMaxHeightExceeded(selectedPlans, plans.rows, maxTableHeightInPx, data.planId, includeRestrictionNotes);
				if (heightMaxReached) {
					showMaxPlansMessage();
					return false;
				}
			}
			if (!checkMaxFormularyPlans(data) || !checkMaxRestrictionPlans(data) || !checkMaxSpecialtyPlans(data)) {
				return false;
			}
			setSelectedPlans((originalPlans) => [...originalPlans, data]);
			return true;
		}
		if (toInclude && (!checkMaxFormularyPlans(data) || !checkMaxRestrictionPlans(data) || !checkMaxSpecialtyPlans(data))) {
			return false;
		}

		setSelectedPlans((originalPlans) => [...originalPlans.filter((p) => p.planId !== plan.planId), data]);
		return true;
	}

	function showMaxPlansMessage() {
		const errorMessage = getConfigValue(appState.config?.PlanList?.UserMessages?.MaxPrintablePlans?.Body, appState)?.Value;
		triggerToastAlert(
			getConfigValue(appState.config?.PlanList?.UserMessages?.MaxPrintablePlans?.Title, appState)?.Value,
			Mustache.render(errorMessage, planListConfig),
			false
		);
	}

	// check if max number of restriction notes
	function checkMaxRestrictionPlans(plan) {
		if (!plan.includeRestrictions && plan.includeCompetitiveRestrictionNotes?.filter((p) => p.includeRestrictions).length === 0) {
			return true;
		}
		const maxRestrictionPlans = getConfigValue(appState.config?.PlanList?.MaxRestrictionPlans, appState)?.Value;
		if (!maxRestrictionPlans) {
			return true;
		}
		const selectedRestrictionPlans = selectedPlans.filter((p) => p.includeRestrictions || p.includeCompetitiveRestrictionNotes?.filter((c) => c.includeRestrictions).length > 0);
		//Check if the plan is already selected for restrictions
		if (selectedRestrictionPlans.find(p => p.planId === plan.planId)) {
			return true;
		}
		if (selectedRestrictionPlans.length >= parseInt(maxRestrictionPlans)) {
			const errorMessage = getConfigValue(appState.config?.PlanList?.UserMessages?.MaxRestrictionPlans?.Body, appState)?.Value;
			triggerToastAlert(
				getConfigValue(appState.config?.PlanList.UserMessages?.MaxRestrictionPlans?.Title, appState)?.Value,
				Mustache.render(errorMessage, planListConfig),
				false
			);
			return false;
		}
		return true;
	}

	// check if the plan is marked to include formularies and if the max number of formularies has been reached (in case there is a max)
	function checkMaxFormularyPlans(plan) {
		if (!plan.includeFormularies) {
			return true;
		}
		const maxFormularyPlans = getConfigValue(appState.config?.PlanList?.MaxFormularyPlans, appState)?.Value;
		if (!maxFormularyPlans) {
			return true;
		}
		const selectedFormularyPlans = selectedPlans.filter((p) => p.includeFormularies);
		//Check if the plan is already selected for formulary
		if (selectedFormularyPlans.find(p => p.planId === plan.planId)) {
			return true;
		}
		if (selectedFormularyPlans.length >= parseInt(maxFormularyPlans)) {
			const errorMessage = getConfigValue(appState.config?.PlanList?.UserMessages?.MaxFormularyPlans?.Body, appState)?.Value;
			triggerToastAlert(
				getConfigValue(appState.config?.PlanList?.UserMessages?.MaxFormularyPlans?.Title, appState)?.Value,
				Mustache.render(errorMessage, planListConfig),
				false
			);
			return false;
		}
		return true;
	}

	// check if the plan is marked to include specialty and if the max number of specialty has been reach (in case there is a max)
	function checkMaxSpecialtyPlans(plan) {
		if (!plan.includeSpecialtyPharmacy) {
			return true;
		}
		const maxSpecialtyPharmaciesPlans = getConfigValue(appState.config?.PlanList?.MaxSpecialtyPharmacyPlans, appState)?.Value;
		if (!maxSpecialtyPharmaciesPlans) {
			return true;
		}
		const selectedSpecialtyPharmaciesPlans = selectedPlans.filter((p) => p.includeSpecialtyPharmacy);
		//Check if the plan is already selected for specialty pharmacy
		if (selectedSpecialtyPharmaciesPlans.find(p => p.planId === plan.planId)) {
			return true;
		}
		if (selectedSpecialtyPharmaciesPlans.length >= parseInt(maxSpecialtyPharmaciesPlans)) {
			const errorMessage = getConfigValue(appState.config?.PlanList?.UserMessages?.MaxSpecialtyPharmaciesPlans?.Body, appState)?.Value;
			triggerToastAlert(
				getConfigValue(appState.config?.PlanList?.UserMessages?.MaxSpecialtyPharmaciesPlans?.Title, appState)?.Value,
				Mustache.render(errorMessage, planListConfig),
				false
			);
			return false;
		}
		return true;
	}

	const setActiveIconColor = {
		color: "white"
	}

	// create sort object
	const [sortColumn, setSortColumn] = useState({
		key: null,
		reverse: false
	});

	const getIcon = (sortKey) => {
		if (sortColumn.key === sortKey && sortColumn.reverse) {
			return <span className='fa-solid fa-arrow-up-short-wide' style={setActiveIconColor}></span>;
		}

		if (sortColumn.key === sortKey && !sortColumn.reverse) {
			return <span className="fa-solid fa-arrow-down-short-wide" style={setActiveIconColor}></span>;
		}

		return <span className='fa-solid fa-sort' style={setActiveIconColor} />;
	};


	function sortPlans(sortBy, sortField) {
		let nextSortState;
		if (sortColumn.key !== sortBy) {
			nextSortState = 'asc';
		} else if (sortColumn.reverse === false) {
			nextSortState = 'desc';
		} else {
			nextSortState = null;
		}

		if (nextSortState === null) {
			setPlans(message.dynamicTable);
			setSortColumn({ key: null, reverse: false });
			return;
		}

		const reverse = nextSortState == 'desc';

		const sortedRows = [...plans.rows].sort((a,b) => compareForDynamicTable(sortBy, sortField, reverse, a, b));

		setPlans({...plans, rows: sortedRows});
		setSortColumn({ key: sortBy, reverse: reverse});
	}



	function didLineWrap(e) {
		let element = document.getElementById(e.key);
		if (element) {
			return element.offsetHeight > 30;
		}

		return false;
	}

	function replacePodIconImgAttributesWithLookup(html, srcLookup, newStyle, newAlt) {
		if(html.includes('data-pod-image-key') === false) {
			return html;
		}
		const newHtml = html.replace(/<img\b([^>]*\bdata-pod-image-key\b[^>]*)>/gi, (match, attrs) => {
			// Extract current src value
			const srcMatch = attrs.match(/\bsrc\s*=\s*(['"])(.*?)\1/i);
			const currentSrc = srcMatch ? srcMatch[2] : '';
			// Lookup new src value
			const newSrc = srcLookup[currentSrc] || currentSrc;
			// Add new attributes
			return `<img src="${newSrc}" style="${newStyle}" alt="${newAlt}">`;
		});
		return newHtml;
	}
	useEffect(() => {
		if (!message) {
			return;
		}
		// Update header titles to replace pod icon img src attributes based on config
		const podIconConfig = getConfigValue(appState.config?.DocumentGeneration?.Images?.Table, appState)?.Columns || [];
		const iconsMap = podIconConfig
			.reduce((obj, item) => {obj[item.Title] = item.DataField; return obj;}, {});
		const updatedPlans = {
			...message.dynamicTable,
			rows: message.dynamicTable.rows.map((row) => {
				const newColumns = {};
				for (const columnId of Object.keys(row.columns)) {
					const value = row.columns[columnId];
					newColumns[columnId] = replacePodIconImgAttributesWithLookup(value, iconsMap, 'width:16px; height:16px;', 'POD Icon');
				}
				return {
					...row,
					columns: newColumns,
				};
			}),
		};
		setPlans(updatedPlans);
	}, [appState, message.dynamicTable]);

	return (
		<>
			{/* <!-- Instructions row. May depend on message type --> */}
			<div className='row my-5 my-lg-4 align-items-center justify-content-lg-between'>
				<div className='col-12 col-md-6'>
					<p className='m-0'>{parentState.selectionMessage}</p>
				</div>
				<div className='col-12 col-md-6 d-flex align-middle justify-content-md-end mt-3 mt-md-0'>
					<div className='d-flex flex-row'>
						<button
							onClick={() => updateCollapseExpand(true)}
							disabled={!parentState.exceedMaxColumns}
							className='btn btn-ghost-primary btn-icon btn-sm type-14 me-3'
						>
							{getConfigValue(appState.config?.PlanList?.ExpandAll?.Label, appState)?.Value || null}{' '}
							<span className='fa-regular fa-circle-plus btn-icon'></span>
						</button>
						<button
							onClick={() => updateCollapseExpand(false)}
							disabled={!parentState.exceedMaxColumns}
							className='btn btn-ghost-primary btn-icon btn-sm type-14'
						>
							{getConfigValue(appState.config?.PlanList?.CollapseAll?.Label, appState)?.Value || null}{' '}
							<span className='fa-regular fa-circle-minus btn-icon'></span>
						</button>
					</div>
				</div>
			</div>
			{/* <!-- Plan list --> */}
			<div className='container-xl'>
				{/* <!-- Plan table header row --> */}

				<div className='row pd-plan-table-header align-items-lg-center d-none d-lg-flex py-3'>
				<div className='pd-plan-col col-lg-1 '>
					{selectAllEnabled && onSelectAllClick && (
						<div className='form-check'>
							<input
								className='form-check-input'
								type='checkbox'
								id='selectAllPlansTable'
								checked={selectAllChecked}
								onChange={(e) => onSelectAllClick(e.target.checked)}
							/>
						</div>
					)}
				</div>
					<div className='pd-plan-col col-lg-11'>
						<div className='align-items-lg-center' style={{ display: 'grid', gridTemplateColumns: `${columnsSpec}` }}>
							{plans?.headers?.map((header, index) => {
								return (
									index < parentState.maxDisplayColumns && (
										<div
											id={header.key}
											key={header.key}
											className={didLineWrap(header) ? ' icon-right-aligned' : ''}
											onClick={() => {
												sortPlans(header.key, header.sortField);
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													sortPlans(header.key, header.sortField);
												}
											}}
										>
											<span dangerouslySetInnerHTML={getSanitizedHtml(header.title)}></span>
											<button
												className='btn-ghost btn-table'
												onClick={() => {
													sortPlans(header.key, header.sortField);
												}}
											>
												{getIcon(header.key)}
											</button>
										</div>
									)
								);
							})}
							{rejectClaimEnabled && (
								<div>
									<span
										role='tooltip'
										ref={rejectClaimHeaderTooltipRef}
										className='fa-regular fa-circle-info me-1'
										tabIndex={0}
										onMouseOver={() => setShowRejectClaimHeaderTooltip(true)}
										onFocus={() => setShowRejectClaimHeaderTooltip(true)}
										onMouseOut={() => setShowRejectClaimHeaderTooltip(false)}
										onBlur={() => setShowRejectClaimHeaderTooltip(false)}
									/>
									<Tooltip
										text={getConfigValue(appState.config?.RejectClaim?.Tooltip?.Label, appState)?.Value || 'Reject Claim Information'}
										ref={rejectClaimHeaderTooltipRef}
										showTooltip={showRejectClaimHeaderTooltip}
										index='reject-claim-header'
									/>
									<span>{getConfigValue(appState.config?.RejectClaim?.PlanList?.Header, appState)?.Value || 'Reject Claim Information'}</span>
								</div>
							)}
							{(enableEllipsisButton || plans?.headers?.length > parentState.maxDisplayColumns) && (
								<div className='col-auto'>
									<div style={{ minWidth: '65px' }}></div>
								</div>
							)}
						</div>
					</div>

					{/* <!-- Button - options --> */}

					<div className='pd-plan-col pd-plan-button col-lg-1'></div>
					{/* <!-- Button - more info --> */}

					<div className='pd-plan-col col-lg-1'></div>
				</div>

				{plans?.rows?.map((row, index) => {
					return (
						<PlanListRow
							plan={row}
							key={row.data.dimPlanIdentity}
							onClick={handleCheckboxClick}
							index={index}
							onOptionsClick={onOptionsClick}
							isSingleSelection={parentState.isSingleSelection}
							isImprovedAccessStatic={parentState.isImprovedAccessStatic}
							isExpandAll={isExpandAll}
							selectedPlans={selectedPlans}
							tableColumnHeaders={plans.headers}
							exceedMaxColumns={parentState.exceedMaxColumns}
							toggleCollapse={toggleCollapse}
							formularyDrillDownEnabled={formularyDrillDownEnabled}
							restrictionDrillDownEnabled={restrictionDrillDownEnabled}
							specialtyPharmacyDrillDownEnabled={specialtyPharmacyDrillDownEnabled}
							canSelectFormulary={parentState.selectableOptions.canSelectFormulary}
							canSelectRestrictions={parentState.selectableOptions.canSelectRestrictions}
							canSelectSpecialtyPharmacy={parentState.selectableOptions.canSelectSpecialtyPharmacy}
							columnsSpec={columnsSpec}
							enableInlineIcons={enableInlineIcons}
							rejectClaimEnabled={rejectClaimEnabled && row.data.hasRejectRescuerDetails}
						/>
					);
				})}
			</div>
		</>
	);
}

export default PlanListTable;
